// Quick virtual (CEO 2026-10-10): a one-off virtual without a presentation.
// Optional client → product from our catalogue, a pasted product link (any
// supplier — we read its photos and imprint details), or an uploaded photo →
// the logo → virtuals to review → PDF / share link (approved only) → attach
// to a project later. Off-catalogue products can go on the catalogue list.
//
// Opened from a company store's product (Flow store builder, 2026-10-19):
// ?client=&clientName=&listing=&logo=&colours= fill in the client, that
// product (its store colours ticked) and the store's logo. Nothing is
// generated until the rep clicks Make.
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Check, ImagePlus, Link2, Loader2, PackageSearch, Search, Sparkles, Trash2, Upload, Wand2, X } from "lucide-react";
import { AddProductModal } from "@/components/catalog/AddProductModal";
import { Button } from "@/components/ui/Button";
import { PageHeader } from "@/components/ui/PageHeader";
import { LogoPrepCard, SpendMeter, useVirtualSet, VirtualsWorkspace } from "@/components/virtuals/VirtualsWorkspace";
import {
  type CatalogueProduct,
  ESTIMATED_LABEL,
  errorText,
  type GenerateTarget,
  type ImprintSpec,
  type PageRead,
  specLine,
  virtualsApi,
} from "@/components/virtuals/virtualsApi";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

interface SpecDraft { method: string; location: string; w: string; h: string; d: string; item: string }
type Draft =
  | { key: string; kind: "catalogue"; product: CatalogueProduct; colours: string[] }
  | { key: string; kind: "url"; sourceUrl: string; imageUrl: string; name: string; colour: string; spec: SpecDraft; specFrom: "page" | "ai" | null }
  | { key: string; kind: "upload"; photoKey: string; preview: string; name: string; colour: string; spec: SpecDraft };

const METHODS = ["Screen print", "Pad print", "Laser engraving", "Deboss", "Embroidery", "Full color print", "Etching"];
const emptySpec = (): SpecDraft => ({ method: "Screen print", location: "", w: "", h: "", d: "", item: "" });
const numOrNull = (s: string) => (s.trim() && Number(s) > 0 ? Number(s) : null);
function specOut(s: SpecDraft): Partial<ImprintSpec> {
  const edges = s.item.split(/[x×,\s]+/).map(Number).filter((n) => n > 0);
  return { method: s.method || "Screen print", location: s.location || null, maxWidthIn: numOrNull(s.w), maxHeightIn: numOrNull(s.h), maxDiameterIn: numOrNull(s.d), itemEdgesIn: edges.length >= 2 ? edges : null };
}
const hasSize = (s: SpecDraft) => !!(numOrNull(s.w) || numOrNull(s.d));
let keySeq = 0;
const nextKey = () => `d${++keySeq}`;

export function QuickVirtualPage() {
  const qc = useQueryClient();
  const [client, setClient] = useState<{ id: string; name: string } | null>(null);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [setId, setSetId] = useState<string | null>(null);
  const [polish, setPolish] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [genErrors, setGenErrors] = useState<string[]>([]);
  const setQ = useVirtualSet(setId);

  // Filled in from a company store's product (see header). Once, on open.
  const [params] = useSearchParams();
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current) return;
    prefilled.current = true;
    const id = (k: string) => { const v = params.get(k); return v && /^[0-9a-f-]{36}$/i.test(v) ? v : null; };
    const clientId = id("client");
    const clientName = params.get("clientName")?.trim() || null;
    const listing = id("listing");
    const logo = id("logo");
    if (clientId) setClient({ id: clientId, name: clientName ?? "This account" });
    if (listing) {
      const want = (params.get("colours") ?? "").split(",").map((c) => c.trim().toLowerCase()).filter(Boolean);
      virtualsApi.listing(listing).then((p) => {
        const ticked = p.colours.filter((c) => want.includes(c.colour.toLowerCase())).map((c) => c.colour);
        setDrafts((x) => [...x, { key: nextKey(), kind: "catalogue", product: p, colours: ticked.length ? ticked : p.colours.slice(0, 1).map((c) => c.colour) }]);
      }).catch((e) => setError(errorText(e)));
    }
    if (logo) {
      setBusy("logo");
      virtualsApi.setFromFile(logo, { clientId, title: clientName })
        .then((s) => setSetId(s.id))
        .catch((e) => setError(errorText(e)))
        .finally(() => setBusy(null));
    }
  }, [params]);

  const count = drafts.reduce((n, d) => n + (d.kind === "catalogue" ? Math.max(1, d.colours.length) : 1), 0);

  const makeSetFromUpload = async (f: File) => {
    setBusy("logo");
    setError(null);
    try {
      const s = await virtualsApi.uploadLogo(f, { clientId: client?.id ?? null, title: client?.name ?? null });
      setSetId(s.id);
      if (client) await qc.invalidateQueries({ queryKey: ["virtuals-client-logos", client.id] });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  };
  const makeSetFromFile = async (fileId: string) => {
    setBusy("logo");
    setError(null);
    try {
      setSetId((await virtualsApi.setFromFile(fileId, { clientId: client?.id ?? null, title: client?.name ?? null })).id);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  };

  const generate = async () => {
    if (!setId) return;
    setBusy("generate");
    setError(null);
    setGenErrors([]);
    try {
      const targets: GenerateTarget[] = drafts.map((d) =>
        d.kind === "catalogue"
          ? { kind: "catalogue", productId: d.product.productId, colours: d.colours }
          : d.kind === "url"
            ? { kind: "url", sourceUrl: d.sourceUrl, imageUrl: d.imageUrl, name: d.name, colour: d.colour || null, spec: specOut(d.spec) }
            : { kind: "upload", photoKey: d.photoKey, name: d.name, colour: d.colour || null, spec: specOut(d.spec) },
      );
      const r = await virtualsApi.generate(setId, targets, polish);
      setGenErrors(r.errors.map((e) => `${e.productName}${e.colour ? ` (${e.colour})` : ""}: ${e.message}`));
      if (r.virtuals.length) setDrafts([]);
      await qc.invalidateQueries({ queryKey: ["virtual-set", setId] });
      await qc.invalidateQueries({ queryKey: ["virtuals-spend"] });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mx-auto w-full">
      <PageHeader
        eyebrow="Sales / Virtuals"
        title="Quick virtual"
        intro="Any product, any logo — a true-to-scale virtual in under a minute. Nothing reaches a client until you approve it."
        action={<SpendMeter />}
      />

      <div className="grid gap-5 lg:grid-cols-[1fr_340px]">
        <div className="space-y-5">
          <Step n={1} title="Client" hint="Optional — saves the logo to their library.">
            <ClientPicker value={client} onChange={setClient} />
          </Step>
          <Step n={2} title="Products" hint="From our catalogue, any product page, or a photo.">
            <ProductAdder onAdd={(d) => setDrafts((x) => [...x, d])} />
            {drafts.length > 0 && (
              <ul className="mt-4 divide-y divide-rule rounded-lg border border-rule">
                {drafts.map((d) => (
                  <DraftRow key={d.key} d={d} onChange={(nd) => setDrafts((x) => x.map((y) => (y.key === d.key ? nd : y)))} onRemove={() => setDrafts((x) => x.filter((y) => y.key !== d.key))} />
                ))}
              </ul>
            )}
          </Step>
        </div>

        <aside className="space-y-5">
          <Step n={3} title="Logo" hint="PNG, JPG, SVG, PDF, AI or EPS. Background removed, colors counted.">
            <LogoPicker clientId={client?.id ?? null} busy={busy === "logo"} onUpload={makeSetFromUpload} onPick={makeSetFromFile} />
            {setQ.data && <div className="mt-3"><LogoPrepCard set={setQ.data} /></div>}
          </Step>
          <div className="rounded-lg border border-rule bg-white p-4">
            <label className="flex cursor-pointer items-start gap-3">
              <input type="checkbox" checked={polish} onChange={(e) => setPolish(e.target.checked)} className="mt-1 h-4 w-4 accent-ink" />
              <span>
                <span className="flex items-center gap-1.5 text-sm font-medium text-ink"><Sparkles className="h-4 w-4" aria-hidden /> AI polish</span>
                <span className="block text-xs text-muted">Curve, texture and light. Thrown away if it changes the logo.</span>
              </span>
            </label>
            <p className="mt-3 text-xs text-muted">Estimated AI cost: <span className="tabular-nums text-ink">${((count * (2 + (polish ? 4 : 0))) / 100).toFixed(2)}</span></p>
          </div>
          {error && <p className="rounded-lg border border-risk/20 bg-risk-bg px-3 py-2 text-sm text-risk" role="alert">{error}</p>}
          <Button size="lg" className="w-full" disabled={!setId || !drafts.length || !!busy} onClick={() => void generate()}>
            {busy === "generate" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            {busy === "generate" ? "Making virtuals…" : `Make ${count || ""} virtual${count === 1 ? "" : "s"}`}
          </Button>
          {!setId && drafts.length > 0 && <p className="text-center text-xs text-muted">Add the logo to continue.</p>}
        </aside>
      </div>

      {genErrors.length > 0 && (
        <div className="mt-5 rounded-lg border border-warn/30 bg-warn-bg px-4 py-3 text-sm text-warn" role="alert">
          <p className="mb-1 flex items-center gap-1.5 font-medium"><AlertTriangle className="h-4 w-4" aria-hidden /> Some virtuals couldn't be made</p>
          <ul className="list-disc pl-5">{genErrors.map((e) => <li key={e}>{e}</li>)}</ul>
        </div>
      )}
      {setQ.data && setQ.data.virtuals.length > 0 && (
        <div className="mt-8"><VirtualsWorkspace set={setQ.data} allowAttach /></div>
      )}
    </div>
  );
}

function Step(props: { n: number; title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-rule bg-white p-4" aria-label={props.title}>
      <div className="mb-3 flex items-baseline gap-2">
        <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-ink text-xs font-semibold text-white">{props.n}</span>
        <h2 className="text-base font-semibold text-ink">{props.title}</h2>
        {props.hint && <span className="text-xs text-muted">{props.hint}</span>}
      </div>
      {props.children}
    </section>
  );
}

function ClientPicker(props: { value: { id: string; name: string } | null; onChange: (c: { id: string; name: string } | null) => void }) {
  const [search, setSearch] = useState("");
  const q = useQuery({
    queryKey: ["virtuals-clients", search],
    queryFn: () => api<{ rows: Array<{ id: string; name: string }> }>(`/clients?limit=8&search=${encodeURIComponent(search)}`),
    enabled: search.trim().length >= 2,
  });
  if (props.value) {
    return (
      <div className="flex items-center justify-between rounded-md border border-rule bg-canvas px-3 py-2 text-sm">
        <span className="font-medium text-ink">{props.value.name}</span>
        <button type="button" onClick={() => props.onChange(null)} aria-label="Remove client" className="text-muted hover:text-ink"><X className="h-4 w-4" /></button>
      </div>
    );
  }
  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted" aria-hidden />
      <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search accounts (or skip)" className="h-10 w-full rounded-md border border-rule pl-9 pr-3 text-sm" aria-label="Search accounts" />
      {search.trim().length >= 2 && (
        <ul className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border border-rule bg-white shadow-lg">
          {(q.data?.rows ?? []).map((c) => (
            <li key={c.id}><button type="button" onClick={() => { props.onChange({ id: c.id, name: c.name }); setSearch(""); }} className="w-full px-3 py-2 text-left text-sm hover:bg-cream-100">{c.name}</button></li>
          ))}
          {q.data && !q.data.rows.length && <li className="px-3 py-2 text-sm text-muted">No accounts match.</li>}
        </ul>
      )}
    </div>
  );
}

function LogoPicker(props: { clientId: string | null; busy: boolean; onUpload: (f: File) => void; onPick: (fileId: string) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const logos = useQuery({ queryKey: ["virtuals-client-logos", props.clientId], queryFn: () => virtualsApi.clientLogos(props.clientId!), enabled: !!props.clientId });
  return (
    <div>
      {!!logos.data?.length && (
        <div className="mb-2 grid grid-cols-4 gap-2">
          {logos.data.slice(0, 8).map((l) => (
            <button key={l.fileId} type="button" onClick={() => props.onPick(l.fileId)} title={l.filename} className="flex aspect-square items-center justify-center rounded-md border border-rule p-1 hover:border-ink">
              <img src={l.url} alt={l.filename} className="max-h-full max-w-full object-contain" />
            </button>
          ))}
        </div>
      )}
      <Button variant="outline" className="w-full" disabled={props.busy} onClick={() => ref.current?.click()}>
        {props.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} {props.busy ? "Preparing the logo…" : "Upload logo"}
      </Button>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif,application/pdf,application/postscript,application/illustrator,.ai,.eps,.pdf" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) props.onUpload(f); }} data-testid="logo-input" />
    </div>
  );
}

function ProductAdder(props: { onAdd: (d: Draft) => void }) {
  const [tab, setTab] = useState<"catalogue" | "url" | "upload">("catalogue");
  const [catalogueOpen, setCatalogueOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [page, setPage] = useState<PageRead | null>(null);
  const [blocked, setBlocked] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [urlDraft, setUrlDraft] = useState<{ imageUrl: string; name: string; colour: string; spec: SpecDraft } | null>(null);
  const [upload, setUpload] = useState<{ photoKey: string; preview: string; name: string; colour: string; spec: SpecDraft } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const read = async () => {
    setBusy(true);
    setErr(null);
    setBlocked(null);
    setPage(null);
    try {
      const r = await virtualsApi.readUrl(url.trim());
      if (!r.ok) { setBlocked(r.message); return; }
      setPage(r);
      const s = r.spec;
      setUrlDraft({
        imageUrl: r.images[0]!.url,
        name: r.name ?? "",
        colour: r.images[0]!.colour ?? s.colours[0] ?? "",
        spec: { method: s.method ?? "Screen print", location: s.location ?? "", w: s.maxWidthIn ? String(round(s.maxWidthIn)) : "", h: s.maxHeightIn ? String(round(s.maxHeightIn)) : "", d: s.maxDiameterIn ? String(round(s.maxDiameterIn)) : "", item: s.itemEdgesIn ? s.itemEdgesIn.map(round).join(" x ") : "" },
      });
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const onPhoto = async (f: File) => {
    setBusy(true);
    setErr(null);
    try {
      const r = await virtualsApi.uploadPhoto(f);
      setUpload({ photoKey: r.photoKey, preview: URL.createObjectURL(f), name: upload?.name || urlDraft?.name || f.name.replace(/\.[a-z]+$/i, ""), colour: "", spec: upload?.spec ?? urlDraft?.spec ?? emptySpec() });
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="mb-3 flex gap-1 rounded-md bg-canvas p-1" role="tablist" aria-label="Where's the product from">
        {([["catalogue", "Our catalogue", PackageSearch], ["url", "Product link", Link2], ["upload", "Upload a photo", Upload]] as const).map(([k, label, Icon]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} className={cn("flex h-9 flex-1 items-center justify-center gap-1.5 rounded text-sm", tab === k ? "bg-white font-medium text-ink shadow-sm" : "text-muted hover:text-ink")}>
            <Icon className="h-4 w-4" aria-hidden /> {label}
          </button>
        ))}
      </div>

      {tab === "catalogue" && (
        <div>
          <Button variant="outline" onClick={() => setCatalogueOpen(true)}><Search className="h-4 w-4" /> Search the catalogue</Button>
          <p className="mt-2 text-xs text-muted">Uses the supplier's blank photos per color and their imprint area and item size.</p>
          {catalogueOpen && (
            <AddProductModal
              onClose={() => setCatalogueOpen(false)}
              onAdd={async (row) => {
                const p = await virtualsApi.listing(row.id);
                props.onAdd({ key: nextKey(), kind: "catalogue", product: p, colours: p.colours.slice(0, 1).map((c) => c.colour) });
              }}
            />
          )}
        </div>
      )}

      {tab === "url" && (
        <div className="space-y-3">
          <div className="flex gap-2">
            <input value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && url.trim() && void read()} placeholder="Paste any product page — a supplier we don't carry is fine" className="h-10 flex-1 rounded-md border border-rule px-3 text-sm" aria-label="Product page link" />
            <Button onClick={() => void read()} disabled={!url.trim() || busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Read page"}</Button>
          </div>
          {blocked && (
            <div className="rounded-md border border-warn/30 bg-warn-bg px-3 py-2 text-sm text-warn" role="alert">
              {blocked}{" "}
              <button type="button" className="font-medium underline" onClick={() => setTab("upload")}>Upload a photo</button>
            </div>
          )}
          {page && urlDraft && (
            <div className="space-y-3 rounded-md border border-rule p-3">
              <p className="text-xs text-muted">From {page.host}{page.spec.source ? ` · imprint details ${page.spec.source === "page" ? "found on the page" : "read from the page by AI — check them"}` : " · no imprint size on the page"}</p>
              <label className="block text-sm"><span className="mb-1 block font-medium text-ink">Product name</span>
                <input value={urlDraft.name} onChange={(e) => setUrlDraft({ ...urlDraft, name: e.target.value })} className="h-10 w-full rounded-md border border-rule px-3" /></label>
              <div>
                <span className="mb-1 block text-sm font-medium text-ink">Photo</span>
                <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
                  {page.images.slice(0, 12).map((im) => (
                    <button key={im.url} type="button" onClick={() => setUrlDraft({ ...urlDraft, imageUrl: im.url, colour: im.colour ?? urlDraft.colour })} aria-pressed={urlDraft.imageUrl === im.url} className={cn("relative aspect-square overflow-hidden rounded-md border bg-canvas", urlDraft.imageUrl === im.url ? "border-ink ring-1 ring-ink" : "border-rule")} title={im.alt ?? undefined}>
                      <img src={im.url} alt={im.alt ?? ""} className="h-full w-full object-contain" referrerPolicy="no-referrer" />
                      {im.colour && <span className="absolute bottom-0 left-0 right-0 truncate bg-white/85 px-1 text-[10px] text-ink">{im.colour}</span>}
                    </button>
                  ))}
                </div>
                <p className="mt-1 text-[11px] text-muted">Pick a plain (blank) photo where you can. A sample logo on the photo is removed by AI when it can be — and flagged when it can't.</p>
              </div>
              <ColourField value={urlDraft.colour} options={page.spec.colours} onChange={(c) => setUrlDraft({ ...urlDraft, colour: c })} />
              <SpecFields value={urlDraft.spec} onChange={(s) => setUrlDraft({ ...urlDraft, spec: s })} />
              <Button onClick={() => { props.onAdd({ key: nextKey(), kind: "url", sourceUrl: page.url, imageUrl: urlDraft.imageUrl, name: urlDraft.name || "Product", colour: urlDraft.colour, spec: urlDraft.spec, specFrom: page.spec.source }); setPage(null); setUrlDraft(null); setUrl(""); }}>
                <Check className="h-4 w-4" /> Add product
              </Button>
            </div>
          )}
        </div>
      )}

      {tab === "upload" && (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {upload ? "Replace photo" : "Choose a product photo"}</Button>
            {upload && <img src={upload.preview} alt="" className="h-14 w-14 rounded-md border border-rule object-contain" />}
          </div>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) void onPhoto(f); }} data-testid="photo-input" />
          {upload && (
            <div className="space-y-3 rounded-md border border-rule p-3">
              <label className="block text-sm"><span className="mb-1 block font-medium text-ink">Product name</span>
                <input value={upload.name} onChange={(e) => setUpload({ ...upload, name: e.target.value })} className="h-10 w-full rounded-md border border-rule px-3" /></label>
              <ColourField value={upload.colour} options={[]} onChange={(c) => setUpload({ ...upload, colour: c })} />
              <SpecFields value={upload.spec} onChange={(s) => setUpload({ ...upload, spec: s })} />
              <Button onClick={() => { props.onAdd({ key: nextKey(), kind: "upload", ...upload, name: upload.name || "Product" }); setUpload(null); }}><Check className="h-4 w-4" /> Add product</Button>
            </div>
          )}
        </div>
      )}
      {err && <p className="mt-2 text-sm text-risk" role="alert">{err}</p>}
    </div>
  );
}

const round = (n: number) => Math.round(n * 100) / 100;

function ColourField(props: { value: string; options: string[]; onChange: (c: string) => void }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-ink">Color <span className="font-normal text-muted">(as shown)</span></span>
      <input list="virtual-colour-options" value={props.value} onChange={(e) => props.onChange(e.target.value)} className="h-10 w-full rounded-md border border-rule px-3" placeholder="e.g. Navy" />
      {props.options.length > 0 && <datalist id="virtual-colour-options">{props.options.map((o) => <option key={o} value={o} />)}</datalist>}
    </label>
  );
}

function SpecFields(props: { value: SpecDraft; onChange: (s: SpecDraft) => void }) {
  const s = props.value;
  const set = (k: keyof SpecDraft) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => props.onChange({ ...s, [k]: e.target.value });
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <label className="block text-sm"><span className="mb-1 block font-medium text-ink">Decoration</span>
          <select value={METHODS.includes(s.method) ? s.method : "__other"} onChange={(e) => props.onChange({ ...s, method: e.target.value === "__other" ? s.method : e.target.value })} className="h-10 w-full rounded-md border border-rule bg-white px-2">
            {METHODS.map((m) => <option key={m}>{m}</option>)}
            {!METHODS.includes(s.method) && <option value="__other">{s.method}</option>}
          </select></label>
        <label className="block text-sm"><span className="mb-1 block font-medium text-ink">Location</span>
          <input value={s.location} onChange={set("location")} placeholder="e.g. Front" className="h-10 w-full rounded-md border border-rule px-3" /></label>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <label className="block text-sm"><span className="mb-1 block font-medium text-ink">Imprint W (in)</span>
          <input inputMode="decimal" value={s.w} onChange={set("w")} placeholder="2.5" className="h-10 w-full rounded-md border border-rule px-3" /></label>
        <label className="block text-sm"><span className="mb-1 block font-medium text-ink">Imprint H (in)</span>
          <input inputMode="decimal" value={s.h} onChange={set("h")} placeholder="1" className="h-10 w-full rounded-md border border-rule px-3" /></label>
        <label className="block text-sm"><span className="mb-1 block font-medium text-ink">Item size (in)</span>
          <input value={s.item} onChange={set("item")} placeholder="8 x 3.5" className="h-10 w-full rounded-md border border-rule px-3" /></label>
      </div>
      {!hasSize(s) && <p className="text-xs text-warn">No imprint size — we'll estimate it from the photo and label the virtual "{ESTIMATED_LABEL}".</p>}
    </div>
  );
}

function DraftRow(props: { d: Draft; onChange: (d: Draft) => void; onRemove: () => void }) {
  const d = props.d;
  const thumb = d.kind === "catalogue" ? d.product.colours.find((c) => d.colours.includes(c.colour))?.photoUrl ?? d.product.colours[0]?.photoUrl : d.kind === "url" ? d.imageUrl : d.preview;
  const name = d.kind === "catalogue" ? d.product.name : d.name;
  const sub = d.kind === "catalogue" ? specLine(d.product.spec) : `${d.kind === "url" ? "Product link" : "Uploaded photo"} · ${specLine({ ...specOut(d.spec), estimated: !hasSize(d.spec) } as ImprintSpec)}`;
  const estimated = d.kind === "catalogue" ? d.product.spec.estimated : !hasSize(d.spec);
  return (
    <li className="flex items-start gap-3 p-3">
      <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-md border border-rule bg-canvas">{thumb && <img src={thumb} alt="" className="h-full w-full object-contain" referrerPolicy="no-referrer" />}</div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-ink">{name}</p>
        <p className={cn("text-xs", estimated ? "text-warn" : "text-muted")}>{sub}{estimated ? " — size will be estimated" : ""}</p>
        {d.kind === "catalogue" && d.product.colours.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5" role="group" aria-label={`Colors for ${name}`}>
            {d.product.colours.map((c) => {
              const on = d.colours.includes(c.colour);
              return (
                <button key={c.colour} type="button" aria-pressed={on} onClick={() => props.onChange({ ...d, colours: on ? d.colours.filter((x) => x !== c.colour) : [...d.colours, c.colour] })} className={cn("inline-flex h-7 items-center gap-1 rounded-md border px-2 text-xs", on ? "border-ink bg-ink text-white" : "border-rule text-ink hover:bg-cream-100")}>
                  {on && <Check className="h-3 w-3" aria-hidden />}{c.colour}
                </button>
              );
            })}
          </div>
        )}
        {d.kind !== "catalogue" && d.colour && <p className="text-xs text-muted">Color: {d.colour}</p>}
      </div>
      <button type="button" onClick={props.onRemove} aria-label={`Remove ${name}`} className="rounded p-1.5 text-muted hover:bg-cream-100 hover:text-ink"><Trash2 className="h-4 w-4" /></button>
    </li>
  );
}

